import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription, Observable } from 'rxjs';
import { mergeMap, tap, take, map, switchMap } from 'rxjs/operators';
import { NgbPaginationConfig } from '@ng-bootstrap/ng-bootstrap';
import { ComponentRef } from '@angular/core';
import { DsButtonVariants, DsTooltipPosition, Icons } from '@levelaccess/design-system';

import { WorkspaceService } from '@client/app/services/workspace.service';
import { $workspace } from '@shared/constants/workspace';
import { $tenant, $privacySettings } from '@shared/constants/tenant';
import { IWorkspace } from '@shared/interfaces/workspace.interface';
import { BrokenLinkData, IScan } from '../../../../../../../shared/interfaces/scan.interface';
import { ScanSummariesService } from '../../../../../services/scan-summaries.service';
import { $scan } from '../../../../../../../shared/constants/scan';
import { LoaderService } from '../../../../../services/loader.service';
import { scanStatus } from '../../../../../../../shared/constants/scanning';
import { AccessibilityAuditToolNames } from '../../../../../../../shared/constants/audit-tool';
import { IPageError } from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { IDclConfig, ITableConfig, ITableRow } from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { DEFAULT_ENTRY_LIMITS, DEFAULT_PAGINATION_MAX_SIZE } from '../../../../../constants/form.constants';
import { TranslateService } from '../../../../../translate/translate.service';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { ICellConfig } from '../../../../../components/table/ngb-table/cells/base-cell/base-cell.component';
import { ITextCellConfig } from '../../../../../components/table/ngb-table/cells/text-cell/text-cell.component';
import { IComposedCellConfig } from '../../../../../components/table/ngb-table/cells/composed-cell/composed-cell.component';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { ModalService } from '../../../../../services/modal.service';
import { OverlayImageComponent } from '../../../../../components/overlay-image/overlay-image.component';
import { IModalDialogInputData } from '../../../../../interfaces/modal-dialog.interface';
import { IImageData } from '../../../../../components/overlay-image/overlay-image.component';
import { ColorFilter } from '../../../../../../../shared/constants/color-filter';
import { UserPropertyService } from '../../../../../services/user-property.service';
import { IDigitalPropertyListItem } from '../../../../../../../shared/interfaces/digital-property.interface';
import { $digitalProperty } from '../../../../../../../shared/constants/digital-properties';

const enum $columns {
  url = 'url',
  reason = 'reason',
  isScannableDocument = 'isScannableDocument',
  errorDetails = 'errorDetails',
  screenshots = 'screenshots',
  expectScreenshot = 'expectScreenshot',
  privacyObfuscationEnabled = 'privacyObfuscationEnabled',
}

interface IPageNotScannedEntry {
  [$columns.url]: string;
  [$columns.reason]: string;
  [$columns.errorDetails]?: string;
  [$columns.screenshots]?: string;
  [$columns.expectScreenshot]?: boolean;
  [$columns.privacyObfuscationEnabled]?: boolean;
}

@Component({
  selector: 'app-view-scan-results-pages-not-scanned',
  templateUrl: './view-scan-results-pages-not-scanned.component.html',
  styleUrls: ['./view-scan-results-pages-not-scanned.component.scss'],
  providers: [NgbPaginationConfig],
  standalone: false,
})
export class ViewScanResultsPagesNotScannedComponent implements OnInit, OnChanges, OnDestroy {
  @Input() private workspaceId: string;
  @Input() private digitalPropertyId: string;
  @Input() private auditTool: AccessibilityAuditToolNames;

  private subscriptions: Subscription;

  @Input() public scan: IScan;

  public entriesLimits: number[];
  public errorsEntriesPerPage: number;
  public skippedEntriesPerPage: number;
  public errorsPage: number;
  public skippedAndBrokenPage: number;
  public errorScreenshotAlt: string;

  public toolErrors: IPageError[];
  public totalErrors: number;
  public getErrors: Subject<void>;
  public getErrors$: Observable<void>;
  public getTotalErrors: Subject<void>;
  public getTotalErrors$: Observable<void>;

  public errorsRawData: IPageNotScannedEntry[];
  public errorsTableData: ITableRow[];

  public skippedAndBrokenRawData: IPageNotScannedEntry[];
  public skippedAndBrokenTableData: ITableRow[];

  private isPrivacyObfuscationEnabled: boolean = false;

  constructor(
    private scanSummariesService: ScanSummariesService,
    private loaderService: LoaderService,
    private ngbPaginationConfig: NgbPaginationConfig,
    private translateService: TranslateService,
    private loadErrorHandler: LoadErrorHandler,
    private featureFlagService: FeatureFlagService,
    private modalService: ModalService,
    private userPropertyService: UserPropertyService,
    private workspaceService: WorkspaceService,
  ) {
    this.ngbPaginationConfig.maxSize = DEFAULT_PAGINATION_MAX_SIZE;
    this.subscriptions = new Subscription();
    this.getErrors = new Subject<void>();
    this.getErrors$ = this.getErrors.asObservable();
    this.getTotalErrors = new Subject<void>();
    this.getTotalErrors$ = this.getTotalErrors.asObservable();

    this.entriesLimits = DEFAULT_ENTRY_LIMITS as number[];
    this.errorsEntriesPerPage = this.entriesLimits[1];
    this.skippedEntriesPerPage = this.entriesLimits[1];
    this.errorsPage = 1;
    this.skippedAndBrokenPage = 1;
    this.errorScreenshotAlt = this.translateService.instant('debug_screenshot');

    this.totalErrors = 0;
    this.errorsTableData = [];
    this.skippedAndBrokenTableData = [];
  }

  private getErrorDataBasedOnErrorInfo(error: IPageError): IPageNotScannedEntry {
    let errorData: IPageNotScannedEntry;
    if (this.featureFlagService.variation(FeatureFlagCollection.scanDebugging, true)) {
      errorData = {
        [$columns.url]: error.url,
        [$columns.reason]: error.error,
        [$columns.screenshots]: error.extras?.appErrorDetails?.screenshot,
        [$columns.expectScreenshot]: error.extras?.expectScreenshot,
        [$columns.privacyObfuscationEnabled]: error.extras?.appErrorDetails?.privacyObfuscationEnabled,
      };
    } else {
      errorData = {
        [$columns.url]: error.url,
        [$columns.reason]: error.error,
      };
    }

    if (SharedCommonUtility.notNullish(error.extras?.errorDetails)) {
      errorData[$columns.errorDetails] = error.extras?.errorDetails;
    }

    return errorData;
  }

  private toErrorData(errors: IPageError[]): IPageNotScannedEntry[] {
    const toErrData = (error: IPageError): IPageNotScannedEntry => {
      return this.getErrorDataBasedOnErrorInfo(error);
    };

    return errors ? errors.map(toErrData) : [];
  }

  private toTableRow(errors: IPageNotScannedEntry[]): ITableRow[] {
    const toRow = (error: IPageNotScannedEntry): ITableRow => {
      let errorCell: IDclConfig<IComposedCellConfig> | IDclConfig<ITextCellConfig> = NgbTableUtilities.textCell({
        text: error[$columns.reason],
        attributes: {
          style: 'word-break: break-word; white-space: break-spaces',
        },
      });

      if (SharedCommonUtility.notNullish(error[$columns.errorDetails])) {
        const stackTraceCell: IDclConfig<ICellConfig> = NgbTableUtilities.tooltipCell({
          text: error[$columns.errorDetails],
        });

        errorCell = NgbTableUtilities.composedCell({ configs: [errorCell, stackTraceCell] });
      }

      let screenshotCell: IDclConfig<ICellConfig> | IDclConfig<ITextCellConfig> = null;

      const screenshot = error[$columns.screenshots];
      const hasScreenshot = SharedCommonUtility.notNullish(screenshot);

      const errorDetails = error[$columns.errorDetails] || '';
      const hasDebugError = error[$columns.expectScreenshot] === true || /AUFActionError|SWAError/.test(errorDetails);

      if (hasScreenshot || hasDebugError) {
        const isPrivacyObfuscationEnabled = this.isPrivacyObfuscationEnabled || error[$columns.privacyObfuscationEnabled];

        screenshotCell = isPrivacyObfuscationEnabled
          ? NgbTableUtilities.tooltipCell({
              text: this.translateService.instant('screenshots_not_available_confidential_scanning'),
              translate: true,
              icon: Icons.EyeSlash,
              tooltipPlacement: DsTooltipPosition.top,
              ariaLabel: 'screenshot_hidden_tooltip_label',
              innerRefStyles: {
                padding: '0 2rem',
              },
            })
          : NgbTableUtilities.DSbuttonCell({
              text: this.translateService.instant('view_debug_screenshot'),
              ariaLabel: this.translateService.instant('view_debug_screenshot_label', error[$columns.url]),
              variant: DsButtonVariants.primary,
              clickListener: () => {
                this.openFullScreen(screenshot, this.errorScreenshotAlt);
              },
            });
      }

      return {
        data: {
          [$columns.url]: NgbTableUtilities.externalLinkCell({
            text: error[$columns.url],
            attributes: {
              href: error[$columns.url],
              style: 'word-break: break-word',
            },
          }),
          [$columns.reason]: errorCell,
          [$columns.screenshots]: screenshotCell,
        },
      };
    };

    return errors.map(toRow);
  }

  private getTenantLevelConfidentialScanning(workspaceId: string): Observable<boolean> {
    return this.workspaceService.getWorkspace(workspaceId).pipe(
      take(1),
      map((workspace: IWorkspace): boolean => {
        return Boolean(workspace?.[$workspace.tenant]?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled]);
      }),
    );
  }

  private subscribeToTotalScanErrors(): void {
    this.subscriptions.add(
      this.getTotalErrors$
        .pipe(
          mergeMap((): Observable<number> => {
            this.loaderService.skipNext();
            return this.scanSummariesService.getTotalScanErrors(
              this.workspaceId,
              this.digitalPropertyId,
              this.scan[$scan._id],
              this.auditTool,
            );
          }),
          tap((totalErrors: number) => {
            this.totalErrors = totalErrors;
          }),
        )
        .subscribe(),
    );
  }

  private subscribeToScanErrors(): void {
    this.subscriptions.add(
      this.getErrors$
        .pipe(
          mergeMap((): Observable<IPageError[]> => {
            this.loaderService.skipNext();
            return this.scanSummariesService.getScanErrors(
              this.workspaceId,
              this.digitalPropertyId,
              this.scan[$scan._id],
              this.auditTool,
              this.errorsEntriesPerPage,
              this.errorsPage,
            );
          }),
        )
        .subscribe({
          next: this.onGetScanErrorsSuccess.bind(this),
          error: this.onGetScanErrorsError.bind(this),
        }),
    );
  }

  public onGetScanErrorsSuccess(errors: IPageError[]): void {
    this.toolErrors = errors;

    this.errorsRawData = this.toErrorData(this.toolErrors);
    this.errorsTableData = this.toTableRow(this.errorsRawData);
  }

  private onGetScanErrorsError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('error_getting_scan_errors', error, ViewScanResultsPagesNotScannedComponent.name);
  }

  public getTableConfig(captionKey: string): ITableConfig {
    const swaDebugScreenshotEnable = this.featureFlagService.variation(FeatureFlagCollection.scanDebugging, false);

    if (swaDebugScreenshotEnable) {
      return {
        columns: {
          [$columns.url]: {
            translationKey: 'label_url',
            styles: {
              width: '35%',
              maxWidth: '35%',
            },
          },
          [$columns.reason]: {
            translationKey: 'column_header_reason',
            styles: {
              width: '35%',
              maxWidth: '35%',
            },
          },
          [$columns.screenshots]: {
            translationKey: 'Screenshots',
            styles: {
              width: '20%',
              maxWidth: '20%',
            },
          },
        },
        caption: captionKey,
      };
    }

    return {
      columns: {
        [$columns.url]: {
          translationKey: 'label_url',
          styles: {
            width: '40%',
            maxWidth: '40%',
          },
        },
        [$columns.reason]: {
          translationKey: 'column_header_reason',
          styles: {
            width: '40%',
            maxWidth: '40%',
          },
        },
      },
      caption: captionKey,
    };
  }

  public shouldShowCard(): boolean {
    const hasSkippedPages: boolean = this.skippedAndBrokenRawData?.length > 0;
    const hasPagesWithErrors: boolean = this.errorsRawData?.length > 0;

    return hasSkippedPages || hasPagesWithErrors;
  }

  public getPaginationLabel(page: number, entriesPerPage: number, total: number): string {
    return this.translateService.instant('n_of_t', [
      `${Math.min((page - 1) * entriesPerPage + 1, total)} - ${Math.min(page * entriesPerPage, total)}`,
      String(total),
    ]);
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.userPropertyService
        .currentSelectedPropertyItem()
        .pipe(
          switchMap((digitalPropertyListItem: IDigitalPropertyListItem) => {
            const workspaceId: string = digitalPropertyListItem[$digitalProperty.workspace][$workspace._id];
            const digitalPropertyPrivacyEnabled: boolean =
              (digitalPropertyListItem as IDigitalPropertyListItem)[$digitalProperty.privacyObfuscationEnabled] === true;

            return this.getTenantLevelConfidentialScanning(workspaceId).pipe(
              map((tenantPrivacyEnabled: boolean): boolean => tenantPrivacyEnabled || digitalPropertyPrivacyEnabled),
            );
          }),
        )
        .subscribe((isEnabled: boolean): void => {
          this.isPrivacyObfuscationEnabled = isEnabled;
        }),
    );

    if (this.scan[$scan.status] === scanStatus.completed_with_errors) {
      this.subscribeToTotalScanErrors();
      this.subscribeToScanErrors();

      this.getErrors.next();
      this.getTotalErrors.next();
    }

    const brokenLinkToPageError = (brokenLink: BrokenLinkData): IPageError => {
      return {
        url: brokenLink.url,
        error: this.translateService.instant('broken_link_error_message', brokenLink.responseCode),
      };
    };

    this.skippedAndBrokenRawData = this.toErrorData(this.scan[$scan.skippedPages]);
    this.skippedAndBrokenRawData = this.skippedAndBrokenRawData.concat(
      this.toErrorData(this.scan[$scan.brokenLinks]?.map(brokenLinkToPageError)),
    );
    this.skippedAndBrokenTableData = this.toTableRow(this.skippedAndBrokenRawData);
  }

  public ngOnChanges(): void {
    this.getErrors.next();
    this.getTotalErrors.next();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public openFullScreen(imageSrc: string, alt: string): void {
    const modalComponentRef: ComponentRef<OverlayImageComponent> = this.modalService.open(OverlayImageComponent);
    const modalData: IModalDialogInputData<IImageData> = {
      data: {
        alt,
        imageSrc,
        filter: ColorFilter.none,
      },
    };

    modalComponentRef.instance.modalInputData = modalData;
  }
}
